import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { GlobalsService } from '../../services/globals.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
	selector: 'app-sales-areas',
	templateUrl: './sales-areas.component.html',
	styleUrls: ['./sales-areas.component.scss']
})
export class SalesAreasComponent implements OnInit {

	@ViewChild('areaModal') areaModalRef: ElementRef;
	@ViewChild('territoryModal') territoryModalRef: ElementRef;

	isLoading = false;
	color: any = 'blue';

	areas: any[] = [];
	territories: any[] = [];

	areaForm: UntypedFormGroup;
	territoryForm: UntypedFormGroup;

	editingArea = false;

	areaSearch = new UntypedFormControl('');
	territorySearch = new UntypedFormControl('');

	areaSortField = 'areadescription';
	areaSortAsc = true;
	terrSortField = 'territory_name';
	terrSortAsc = true;

	constructor(
		private globalsService: GlobalsService,
		private fb: UntypedFormBuilder,
		private location: Location,
		public router: Router,
		private globalSearchService: GlobalSearchService,
		private modalService: NgbModal
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.areaForm = this.fb.group({
			id: [''],
			areacode: ['', Validators.required],
			areadescription: ['', Validators.required],
		});

		this.territoryForm = this.fb.group({
			territory_id: [''],
			territory_name: ['', Validators.required],
		});

		this.loadData();
	}

	loadData() {
		this.isLoading = true;
		this.globalsService.getAreasList().subscribe((results: any) => {
			this.areas = Array.isArray(results) ? results : [];
			this.globalSearchService.areas.next(this.areas);
			this.globalsService.getTerritories().subscribe((tresults: any) => {
				this.isLoading = false;
				this.territories = Array.isArray(tresults) ? tresults : [];
			});
		});
	}

	// ---- Derived (search + sort) ----

	get filteredAreas(): any[] {
		const term = (this.areaSearch.value || '').toString().toLowerCase().trim();
		let rows = this.areas;
		if (term) {
			rows = rows.filter(a =>
				(a.areacode || '').toString().toLowerCase().includes(term) ||
				(a.areadescription || '').toString().toLowerCase().includes(term));
		}
		return this.sortRows(rows, this.areaSortField, this.areaSortAsc);
	}

	get filteredTerritories(): any[] {
		const term = (this.territorySearch.value || '').toString().toLowerCase().trim();
		let rows = this.territories;
		if (term) {
			rows = rows.filter(t =>
				(t.territory_name || '').toString().toLowerCase().includes(term) ||
				(t.territory_id || '').toString().toLowerCase().includes(term));
		}
		return this.sortRows(rows, this.terrSortField, this.terrSortAsc);
	}

	private sortRows(rows: any[], field: string, asc: boolean): any[] {
		const dir = asc ? 1 : -1;
		const numeric = field === 'usage_count' || field === 'territory_id';
		return [...rows].sort((a, b) => {
			let av = a[field];
			let bv = b[field];
			if (numeric) {
				return ((parseFloat(av) || 0) - (parseFloat(bv) || 0)) * dir;
			}
			av = (av || '').toString().toLowerCase();
			bv = (bv || '').toString().toLowerCase();
			return av.localeCompare(bv) * dir;
		});
	}

	setAreaSort(field: string) {
		if (this.areaSortField === field) {
			this.areaSortAsc = !this.areaSortAsc;
		} else {
			this.areaSortField = field;
			this.areaSortAsc = true;
		}
	}

	setTerrSort(field: string) {
		if (this.terrSortField === field) {
			this.terrSortAsc = !this.terrSortAsc;
		} else {
			this.terrSortField = field;
			this.terrSortAsc = true;
		}
	}

	// ---- Sales Areas ----

	addArea() {
		this.editingArea = false;
		this.areaForm = this.fb.group({
			id: [''],
			areacode: ['', Validators.required],
			areadescription: ['', Validators.required],
		});
		this.modalService.open(this.areaModalRef, { ariaLabelledBy: 'modal-title', size: 'lg' });
	}

	editArea(area: any) {
		this.editingArea = true;
		this.areaForm = this.fb.group({
			id: [area.areacode],
			areacode: [{ value: area.areacode, disabled: true }, Validators.required],
			areadescription: [area.areadescription, Validators.required],
		});
		this.modalService.open(this.areaModalRef, { ariaLabelledBy: 'modal-title', size: 'lg' });
	}

	saveArea() {
		this.globalsService.saveArea(this.areaForm.getRawValue()).subscribe(result => {
			if (result.success) {
				this.areas = Array.isArray(result.data) ? result.data : [];
				this.globalSearchService.areas.next(this.areas);
				this.modalService.dismissAll();
			} else {
				this.globalSearchService.showNotification(result.message, 'danger', 'top', 'right');
			}
		});
	}

	removeArea(area: any) {
		if (Number(area.usage_count) > 0) { return; }
		if (confirm('Remove Sales Area ' + area.areacode + '?')) {
			this.globalsService.removeArea(area).subscribe(result => {
				if (result.success) {
					this.areas = Array.isArray(result.data) ? result.data : [];
					this.globalSearchService.areas.next(this.areas);
				} else {
					this.globalSearchService.showNotification(result.message, 'danger', 'top', 'right');
				}
			});
		}
	}

	// ---- Sales Territories ----

	addTerritory() {
		this.territoryForm = this.fb.group({
			territory_id: [''],
			territory_name: ['', Validators.required],
		});
		this.modalService.open(this.territoryModalRef, { ariaLabelledBy: 'modal-title', size: 'lg' });
	}

	editTerritory(territory: any) {
		this.territoryForm = this.fb.group({
			territory_id: [territory.territory_id],
			territory_name: [territory.territory_name, Validators.required],
		});
		this.modalService.open(this.territoryModalRef, { ariaLabelledBy: 'modal-title', size: 'lg' });
	}

	saveTerritory() {
		this.globalsService.saveTerritory(this.territoryForm.value).subscribe(result => {
			if (result.success) {
				this.territories = Array.isArray(result.data) ? result.data : [];
				this.modalService.dismissAll();
			} else {
				this.globalSearchService.showNotification(result.message, 'danger', 'top', 'right');
			}
		});
	}

	removeTerritory(territory: any) {
		if (Number(territory.usage_count) > 0) { return; }
		if (confirm('Remove Territory ' + territory.territory_name + '?')) {
			this.globalsService.removeTerritory(territory).subscribe(result => {
				if (result.success) {
					this.territories = Array.isArray(result.data) ? result.data : [];
				} else {
					this.globalSearchService.showNotification(result.message, 'danger', 'top', 'right');
				}
			});
		}
	}

	back(): void {
		this.location.back();
	}
}
